import { type ReactElement, Suspense } from 'react';
import { useWidgetState } from '../../management/customDashboard/CustomWidgetContext.tsx';
import type { PortfolioCorrelationsWidgetState } from './PortfolioCorrelationsWidgetSettings.tsx';
import { HistoricalPortfolioCorrelationsChart } from './HistoricalPortfolioCorrelations.tsx';
import { usePortfolioCorrelationsQuery, IWindowType } from '../../../generated/graphql.tsx';
import { convertDateRangeToSinceToDate } from '../../dateRange.utils.ts';
import { getRangeByLabel, getDefaultRange } from '../../predefinedDateRanges.ts';
import Loader from '../../technical/Loader/Loader.tsx';
import { Stack, Typography } from '@mui/joy';
import WarningMessage from '../../technical/WarningMessage/WarningMessage.tsx';
import { isWarmUpEnabled } from './PortfolioCorrelations.utils.ts';

const PortfolioCorrelationsWidgetView = (props: { id: string }): ReactElement => {
  const state = useWidgetState<PortfolioCorrelationsWidgetState>(props.id);

  return (
    <Suspense fallback={<Loader />}>
      <PortfolioCorrelationsWidgetViewInner state={state} />
    </Suspense>
  );
};

const PortfolioCorrelationsWidgetViewInner = ({
  state,
}: {
  state: PortfolioCorrelationsWidgetState;
}): ReactElement => {
  const range = getRangeByLabel(state.predefinedDateRange) ?? getDefaultRange();
  const dateRange = range.value();

  const hasEnoughPortfolios = state.portfolioIds && state.portfolioIds.length >= 2;

  const { data, loading, error } = usePortfolioCorrelationsQuery({
    variables: {
      input: {
        portfolios: state.portfolioIds || [],
        dateRange: convertDateRangeToSinceToDate(dateRange),
        windowParams: {
          windowType: state.windowType,
          windowSize: state.windowType === IWindowType.Rolling ? state.windowSize : undefined,
        },
      },
    },
    skip: !hasEnoughPortfolios,
  });

  if (!hasEnoughPortfolios) {
    return (
      <Stack
        sx={{
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 2,
        }}
      >
        <WarningMessage>
          <Typography level="body-md" textAlign="center">
            Please configure at least 2 portfolios in the widget settings to display correlations.
          </Typography>
        </WarningMessage>
      </Stack>
    );
  }

  const warmupEnabled = isWarmUpEnabled(state.windowType);

  return (
    <HistoricalPortfolioCorrelationsChart
      data={data}
      loading={loading}
      error={error}
      warmup={state.warmUpPeriod}
      warmupEnabled={warmupEnabled}
      fullHeight
    />
  );
};

export default PortfolioCorrelationsWidgetView;
